<script setup>
	import { onBeforeUnmount, watch } from "vue";

	const props = defineProps({
		modelValue: {
			type: Boolean,
			default: false,
		},
	});

	const emit = defineEmits(["update:modelValue"]);

	function close() {
		emit("update:modelValue", false);
	}

	function handleKeydown(event) {
		if (event.key === "Escape") {
			close();
		}
	}

	watch(() => props.modelValue, (isOpen) => {
		if (typeof document === "undefined") {
			return;
		}

		if (isOpen) {
			const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
			document.documentElement.style.overflow = "hidden";
			document.body.style.overflow = "hidden";

			if (scrollbarWidth > 0) {
				document.body.style.paddingRight = `${scrollbarWidth}px`;
			}

			window.addEventListener("keydown", handleKeydown);
		} else {
			document.documentElement.style.overflow = "";
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
			window.removeEventListener("keydown", handleKeydown);
		}
	});

	onBeforeUnmount(() => {
		if (typeof document !== "undefined") {
			document.documentElement.style.overflow = "";
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
			window.removeEventListener("keydown", handleKeydown);
		}
	});
</script>

<template>
	<Teleport to="body">
		<transition name="lux-dialog-fade">
			<div v-if="modelValue" class="lux-dialog-overlay" @click.self="close">
				<div class="lux-dialog-card" role="dialog" aria-modal="true" aria-labelledby="termsDialogTitle" @click.stop>
					<button type="button" class="lux-dialog-close" aria-label="Close" @click="close">&times;</button>

					<div class="lux-dialog-scroll">
						<header class="lux-dialog-head">
							<span class="lux-dialog-eyebrow">Lucentary</span>
							<h2 id="termsDialogTitle" class="lux-dialog-title">Terms of Use</h2>
							<p class="lux-dialog-meta">Effective Date: October 29, 2024</p>
						</header>

						<div class="lux-dialog-body">
							<p>These Terms of Use ("Terms") govern your access to and use of the website https://lucentary.com (the "Site") and the services provided by Lucentary Academy Inc d/b/a Lucentary ("Lucentary," "we," "us," or "our"). By accessing or using the Site or our services, you agree to be bound by these Terms. If you do not agree with any part of these Terms, please do not use the Site or our services.</p>

							<h3>1. Acceptance of Terms</h3>
							<p>By accessing or using the Site, you confirm that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you are using the Site on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.</p>

							<h3>2. Eligibility</h3>
							<p>You must be at least 18 years of age and able to form a legally binding contract to use the Site or our services. By using the Site, you represent and warrant that you meet these requirements.</p>

							<h3>3. Our Services</h3>
							<p>Lucentary provides software consulting, design, development, and related professional services. Any specific engagement is governed by a separate written agreement or statement of work between you and Lucentary. In the event of a conflict between these Terms and a signed agreement for services, the signed agreement will control with respect to that engagement.</p>

							<h3>4. Use of the Site</h3>
							<p>We grant you a limited, non-exclusive, non-transferable, and revocable license to access and use the Site for your personal or internal business purposes. You agree not to use the Site in any way that could damage, disable, overburden, or impair it, or interfere with any other party's use of the Site.</p>

							<h3>5. Acceptable Use</h3>
							<p>When using the Site, you agree that you will not:</p>
							<ul>
								<li>Use the Site for any unlawful purpose or in violation of any applicable law or regulation;</li>
								<li>Attempt to gain unauthorized access to any portion of the Site, other accounts, or our systems or networks;</li>
								<li>Introduce viruses, malware, or any other malicious or harmful code;</li>
								<li>Copy, scrape, reverse engineer, or attempt to derive the source code of any part of the Site except as permitted by law;</li>
								<li>Infringe upon the intellectual property or other rights of Lucentary or any third party.</li>
							</ul>

							<h3>6. Intellectual Property</h3>
							<p>All content on the Site, including text, graphics, logos, the Lucentary name and marks, and software, is the property of Lucentary or its licensors and is protected by applicable intellectual property laws. You may not use, reproduce, or distribute any such content without our prior written permission.</p>

							<h3>7. Client Materials and Confidentiality</h3>
							<p>Any confidential information exchanged in connection with a services engagement will be handled in accordance with the applicable signed agreement. You retain ownership of materials you provide to us, and you grant us the limited rights necessary to perform the agreed-upon services.</p>

							<h3>8. Third-Party Links</h3>
							<p>The Site may contain links to third-party websites or resources that we do not own or control. We are not responsible for the content, policies, or practices of any third-party sites, and you access them at your own risk.</p>

							<h3>9. Disclaimer of Warranties</h3>
							<p>The Site and its content are provided on an "as is" and "as available" basis, without warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Site will be uninterrupted, error-free, or free of harmful components.</p>

							<h3>10. Limitation of Liability</h3>
							<p>To the fullest extent permitted by law, Lucentary will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or data, arising out of or related to your use of the Site. Our total liability for any claim arising out of or relating to the Site will not exceed one hundred U.S. dollars (USD $100), unless otherwise set out in a signed agreement for services.</p>

							<h3>11. Indemnification</h3>
							<p>You agree to indemnify and hold harmless Lucentary and its officers, employees, and agents from any claims, damages, liabilities, and expenses arising out of your use of the Site or your violation of these Terms.</p>

							<h3>12. Modifications to the Site and Services</h3>
							<p>We may modify, suspend, or discontinue all or any part of the Site or our services at any time, with or without notice. We will not be liable to you or any third party for any such modification, suspension, or discontinuation.</p>

							<h3>13. Changes to These Terms</h3>
							<p>We may update these Terms from time to time. We will notify you of any changes by posting the updated Terms on the Site. Your continued use of the Site after any modifications constitutes your acceptance of the revised Terms.</p>

							<h3>14. Governing Law</h3>
							<p>These Terms are governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law principles. You agree that any dispute arising out of or relating to these Terms or the Site will be subject to the exclusive jurisdiction of the state and federal courts located in Tarrant County, Texas.</p>

							<h3>15. Contact Us</h3>
							<p>If you have any questions about these Terms, please contact us at:</p>
							<address>
								Lucentary<br />
								Email: <a href="mailto:info@lucentary.com" target="_blank" rel="noopener">info@lucentary.com</a><br />
								Website: <a href="https://lucentary.com" target="_blank" rel="noopener">https://lucentary.com</a>
							</address>
						</div>
					</div>

					<footer class="lux-dialog-foot">
						<button type="button" class="lux-dialog-understand" @click="close">I understand</button>
					</footer>
				</div>
			</div>
		</transition>
	</Teleport>
</template>

<style scoped>
	.lux-dialog-overlay {
		position: fixed;
		inset: 0;
		z-index: 100000;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		background: rgba(10, 23, 48, 0.55);
		backdrop-filter: blur(5px);
		-webkit-backdrop-filter: blur(5px);
		cursor: pointer;
		font-family: "Poppins", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
	}

	.lux-dialog-card {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		max-width: 640px;
		max-height: calc(100vh - 3rem);
		max-height: calc(100dvh - 3rem);
		background: #ffffff;
		border-radius: 20px;
		box-shadow: 0 40px 90px -30px rgba(10, 23, 48, 0.6);
		cursor: default;
		overflow: hidden;
	}

	.lux-dialog-close {
		position: absolute;
		top: 1rem;
		right: 1rem;
		z-index: 2;
		width: 40px;
		height: 40px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border: none;
		border-radius: 12px;
		background: #f1f5fb;
		color: #475569;
		font-size: 1.8rem;
		line-height: 1;
		cursor: pointer;
		transition: background 0.18s ease, color 0.18s ease;
	}

	.lux-dialog-close:hover {
		background: #e7eefe;
		color: #1d4ed8;
	}

	.lux-dialog-scroll {
		flex: 1;
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		padding: clamp(1.75rem, 4vw, 2.5rem);
		padding-top: clamp(2.25rem, 4vw, 2.75rem);
	}

	.lux-dialog-head {
		margin-bottom: 1.5rem;
		padding-right: 2.5rem;
	}

	.lux-dialog-eyebrow {
		display: inline-block;
		font-size: 0.78rem;
		font-weight: 700;
		letter-spacing: 0.14em;
		text-transform: uppercase;
		color: #2563eb;
		margin-bottom: 0.5rem;
	}

	.lux-dialog-title {
		font-size: clamp(1.6rem, 3vw, 2.1rem);
		font-weight: 800;
		letter-spacing: -0.01em;
		color: #0d1b34;
		margin: 0 0 0.35rem;
	}

	.lux-dialog-meta {
		font-size: 0.92rem;
		color: #6f7f99;
		margin: 0;
	}

	.lux-dialog-body {
		color: #45556f;
		font-size: 1rem;
		line-height: 1.7;
	}

	.lux-dialog-body h3 {
		font-size: 1.18rem;
		font-weight: 700;
		color: #0f2142;
		margin: 1.75rem 0 0.6rem;
	}

	.lux-dialog-body p {
		margin: 0 0 1rem;
	}

	.lux-dialog-body ul {
		margin: 0 0 1rem;
		padding-left: 1.3rem;
	}

	.lux-dialog-body li {
		margin-bottom: 0.5rem;
	}

	.lux-dialog-body strong {
		color: #0f2142;
		font-weight: 600;
	}

	.lux-dialog-body address {
		font-style: normal;
		line-height: 1.7;
		color: #45556f;
	}

	.lux-dialog-body a {
		color: #2563eb;
		font-weight: 600;
		text-decoration: none;
		cursor: pointer;
		transition: color 0.18s ease;
	}

	.lux-dialog-body a:hover {
		color: #1d4ed8;
		text-decoration: underline;
	}

	.lux-dialog-foot {
		flex-shrink: 0;
		display: flex;
		justify-content: flex-end;
		gap: 0.75rem;
		padding: 1.1rem clamp(1.75rem, 4vw, 2.5rem);
		border-top: 1px solid #e6edf8;
		background: #ffffff;
	}

	.lux-dialog-understand {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.7rem 1.6rem;
		border: none;
		border-radius: 999px;
		background: #2563eb;
		color: #ffffff;
		font-family: inherit;
		font-size: 0.98rem;
		font-weight: 600;
		cursor: pointer;
		box-shadow: 0 12px 24px -12px rgba(37, 99, 235, 0.7);
		transition: transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
	}

	.lux-dialog-understand:hover {
		transform: translateY(-1px);
		background: #1d4ed8;
		box-shadow: 0 16px 30px -14px rgba(37, 99, 235, 0.75);
	}

	.lux-dialog-fade-enter-active,
	.lux-dialog-fade-leave-active {
		transition: opacity 0.25s ease;
	}

	.lux-dialog-fade-enter-active .lux-dialog-card,
	.lux-dialog-fade-leave-active .lux-dialog-card {
		transition: transform 0.25s ease, opacity 0.25s ease;
	}

	.lux-dialog-fade-enter-from,
	.lux-dialog-fade-leave-to {
		opacity: 0;
	}

	.lux-dialog-fade-enter-from .lux-dialog-card,
	.lux-dialog-fade-leave-to .lux-dialog-card {
		transform: translateY(14px) scale(0.985);
		opacity: 0;
	}

	@media (max-width: 560px) {
		.lux-dialog-foot {
			justify-content: stretch;
		}

		.lux-dialog-understand {
			width: 100%;
		}
	}
</style>